import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import { NotificationService, CompSearchService, SessionService, UtilsService, EntitiesService, TpSafeService } from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';
import {
  Container,
  FinancialDataValueEnteredInEnum,
  Entity,
  Transaction,
  LegalEntityTransaction,
  BaseOption,
  UploadReportVersionModalIncomingData,
  LanguageIsoInfo,
} from '../../../shared/models';
import { UploadReportVersionPayload } from '../../../shared/models/request-payload/upload-report-version-payload';
import { PaginationConstants } from '../../../shared/constants';
import { FileConstants } from '../../../shared/constants/file';

@Component({
  selector: 'upload-report-version-modal',
  templateUrl: './upload-report-version-modal.component.html',
  styleUrls: ['./upload-report-version-modal.component.css']
})
export class UploadReportVersionModalComponent
  extends ClosableModalWithErrorResponseModel<any>
  implements OnInit, OnDestroy {

  data: UploadReportVersionModalIncomingData;
  languages: Record<'language_iso', string>[];
  currencyOptions: BaseOption[];
  tradingPartnerEntities: Entity[];
  controlledTransactionsInReport: Transaction[];
  uploadReportVersionForm: FormGroup;

  transactionsOnCurrentPage: Transaction[];
  itemsPerPage: number;

  private uploadedReport: File;
  private workingContainer: Container;
  private selectedStudyId: number;

  private onDestroy$: Subject<null>;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _formBuilder: FormBuilder,
    private _sessionService: SessionService,
    private _compSearchService: CompSearchService,
    private _utilsService: UtilsService,
    private _entitiesService: EntitiesService,
    private _tpSafeService: TpSafeService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit() {
    this.uploadReportVersionForm = this._formBuilder.group({
      file: ['', [Validators.required, this.validateFile.bind(this)]],
      language: ['', Validators.required],
      currencyType: ['', Validators.required],
      tradingPartnerEntity: ['', Validators.required],
      reportStage: ['', Validators.required],
      controlledTransactionsInReport: this._formBuilder.group({}, { validator: this.validateControlledTransactionsInReport }),
    });

    this.workingContainer = this._sessionService.getWorkingContainer();
    this.getLanguages();
    this.getCurrencyOptions();
    this.tradingPartnerEntities = this.data.tradingPartnerEntities;

    this.onDestroy$ = new Subject<null>();
    this.getTradingPartnerEntityFormControl().valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(this.getControlledTransactionsInReport.bind(this));

    this.itemsPerPage = PaginationConstants.DEFAULT_PAGE_SIZE;
  }

  ngOnDestroy() { this.onDestroy$.next(); }

  getFileFormControl(): AbstractControl { return this.uploadReportVersionForm.get('file'); }
  getLanguageFormControl(): AbstractControl { return this.uploadReportVersionForm.get('language'); }
  getCurrencyTypeFormControl(): AbstractControl { return this.uploadReportVersionForm.get('currencyType'); }
  getTradingPartnerEntityFormControl(): AbstractControl { return this.uploadReportVersionForm.get('tradingPartnerEntity'); }
  getReportStageFormControl(): AbstractControl { return this.uploadReportVersionForm.get('reportStage'); }
  getControlledTransactionsInReportFormGroup(): FormGroup { return this.uploadReportVersionForm.get('controlledTransactionsInReport') as FormGroup; }

  getPageSizes(): number[] { return PaginationConstants.PAGE_SIZES; }

  getSelectableTransactions(): Transaction[] {
    let selectableTransactions: Transaction[] = [];

    if (this.controlledTransactionsInReport && this.selectedStudyId) {
      selectableTransactions = this.controlledTransactionsInReport.filter(transaction => transaction.study.studyId === this.selectedStudyId);
    } else if (this.controlledTransactionsInReport) {
      selectableTransactions = this.controlledTransactionsInReport;
    }

    return selectableTransactions;
  }

  getTransactionEntityCode(legalEntityTransactions: LegalEntityTransaction[], isSourceEntity: boolean): string {
    const transactionWithEntityCode = legalEntityTransactions.find(transaction => transaction.isSource === isSourceEntity);
    return transactionWithEntityCode.entity.code;
  }

  onFileSelect(selectedFiles: File[]): void {
    this.uploadedReport = selectedFiles[0];
  }

  onPageChanged(currentPage: number): void {
    this.setTransactionsOnCurrentPage(currentPage);
  }

  onPageSizeChange(pageSize: number): void {
    this.itemsPerPage = pageSize;
    this.setTransactionsOnCurrentPage();
  }

  save(event): void {
    event.preventDefault();
    Object.keys(this.uploadReportVersionForm.controls).forEach((controlKey) => {
      this.uploadReportVersionForm.controls[controlKey].markAsTouched();
    });

    if (this.uploadReportVersionForm.valid) {
      this.isSaveDisabled = true;
      this.getSignedUrlForUploadedReport();
    }
  }

  close(): void {
    this.closeAllModals();
  }

  private getLanguages(): void {
    const jurisdictionId = this.data.entity.taxJurisdiction.countryId;
    const taxYear = this.workingContainer.taxYear;

    this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe(({ data }) => {
      this.languages = data;
      this.setDefaultLanguage(this.languages);
    });
  }

  private setDefaultLanguage(languages: LanguageIsoInfo[]): void {
    if (languages && languages.length > 0) {
      const languageToUseIsoInfo: LanguageIsoInfo = this._utilsService.findDefaultLanguage(languages, this.data.latestGeneratedVersion.languageIso);

      this.getLanguageFormControl().setValue(languageToUseIsoInfo.language_iso);
    }
  }

  private getCurrencyOptions(): void {
    if (this.data &&
      this.data.entity &&
      this.data.entity.legalEntityCurrencyConversion &&
      this.data.entity.legalEntityCurrencyConversion.length) {
      this.currencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
    } else {
      this.currencyOptions = [{ id: 1, name: FinancialDataValueEnteredInEnum[1] }];
    }
    this.setDefaultCurrency();
  }

  setDefaultCurrency(): void {
    const reportCurrencyIsoCode: string = this.data.latestGeneratedVersion.reportCurrency.isoCode;
    const mneCurrencyIsoCode: string = this.data.mneCurrency.isoCode;

    const currencyOptionNumber: number = reportCurrencyIsoCode === mneCurrencyIsoCode ?
      FinancialDataValueEnteredInEnum.MNE :
      FinancialDataValueEnteredInEnum.Entity;

    this.getCurrencyTypeFormControl().setValue(currencyOptionNumber);
  }

  private getControlledTransactionsInReport(): void {
    const entityId = this.data.entity.entityId;
    const tradingPartnerEntityId = this.getTradingPartnerEntityFormControl().value as number;

    this._entitiesService.getEntityPairTransactions(entityId, tradingPartnerEntityId).subscribe((res) => {
      this.controlledTransactionsInReport = res.data;

      this.setUpControlledTransactionsInReportFormControls();
      this.setTransactionsOnCurrentPage();
    });
  }

  private setUpControlledTransactionsInReportFormControls(): void {
    this.controlledTransactionsInReport.forEach((transaction) => {
      this.getControlledTransactionsInReportFormGroup().addControl(String(transaction.transactionId), this._formBuilder.control(''));

      this.getControlledTransactionsInReportFormGroup().controls[transaction.transactionId].valueChanges
        .pipe(debounceTime(0), takeUntil(this.onDestroy$))
        .subscribe(this.filterTransactionsByStudy.bind(this, transaction));
    });
  }

  private filterTransactionsByStudy(transaction: Transaction): void {
    this.selectedStudyId = (this.getControlledTransactionsInReportFormGroup().valid ? transaction.study.studyId : undefined);
    this.setTransactionsOnCurrentPage();
  }

  private setTransactionsOnCurrentPage(currentPage: number = 1): void {
    if (this.controlledTransactionsInReport) {
      const startIdx = (currentPage - 1) * this.itemsPerPage;
      const endIdx = currentPage * this.itemsPerPage;
      this.transactionsOnCurrentPage = this.getSelectableTransactions().slice(startIdx, endIdx);
    }
  }

  private validateFile(fileFormControl: AbstractControl): ValidationErrors | null {
    let validationResult: ValidationErrors | null;

    if (this._utilsService.doesFilenameEndWithPdf(fileFormControl.value)) {
      validationResult = null;
    } else {
      validationResult = { validateFile: { valid: false } };
    }

    return validationResult;
  }

  private validateControlledTransactionsInReport(controlledTransactionsFormGroup: FormGroup): ValidationErrors | null {
    const allControlledTransactionIds = Object.keys(controlledTransactionsFormGroup.controls);
    const anyTransactionsAreSelected = allControlledTransactionIds.some((transactionId) => controlledTransactionsFormGroup.controls[transactionId].value === true);

    let validationResult: ValidationErrors | null;
    if (anyTransactionsAreSelected) {
      validationResult = null;
    } else {
      validationResult = { validateControlledTransactionsInReport: { valid: false } };
    }

    return validationResult;
  }

  private getSignedUrlForUploadedReport(): void {
    const uploadReportVersionPayload = this.createUploadReportVersionPayload();

    this._tpSafeService.getSignedUrlForUploadedReport(uploadReportVersionPayload).subscribe(
      ({ data }) => {
        this.saveUploadedReportToTpSafe(data.url);
      },
      () => this.isSaveDisabled = false
    );
  }

  private saveUploadedReportToTpSafe(signedUrl: string): void {
    this._tpSafeService.saveUploadedReportToTpSafe(signedUrl, this.uploadedReport).subscribe(
      () => {
        this.isSaveDisabled = false;
        this.success();
        this.close();
      },
      () => this.isSaveDisabled = false
    );
  }

  private createUploadReportVersionPayload(): UploadReportVersionPayload {
    const allControlledTransactionsInReport: Record<string, boolean> = this.uploadReportVersionForm.value.controlledTransactionsInReport;
    const selectedTransactionIds = Object.keys(allControlledTransactionsInReport)
      .filter(transactionId => allControlledTransactionsInReport[transactionId] === true)
      .map(Number);

    return {
      reportStage: this.getReportStageFormControl().value,
      currencyId: this.getUploadReportPayloadCurrencyId(),
      internalLocalfileReportId: this.data.internalLocalFile.internalLocalfileReportId,
      primaryEntityId: this.data.entity.entityId,
      tradingPartnerEntityId: Number(this.getTradingPartnerEntityFormControl().value),
      executiveSummaryIntro: null,
      executiveSummaryConclusion: null,
      languageIso: this.getLanguageFormControl().value,
      containerId: this.workingContainer.containerId,
      isUploadedReport: true,
      selectedTransactionIds,
      mimeType: this.uploadedReport.type,
      fileExtension: FileConstants.FILE_EXTENSIONS.PDF,
    };
  }

  private getUploadReportPayloadCurrencyId(): number {
    let currencyId: number;
    const currencyTypeId = Number(this.getCurrencyTypeFormControl().value);

    if (currencyTypeId === 1) {
      currencyId = this.data.mneCurrency.currencyId;
    } else if (currencyTypeId === 2) {
      currencyId = this.data.taxJurisdictionCurrency.currencyId;
    }

    return currencyId;
  }

}
